import { createSlice, PayloadAction } from '@reduxjs/toolkit';

enum LoginMethod {
  CREDENTIALS = 'credentials',
  TELEGRAM = 'telegram',
  GOOGLE = 'google'
}

interface TelegramAuthData {
  id: number;
  first_name: string;
  auth_date: number;
  hash: string;
  last_name?: string;
  photo_url?: string;
  username?: string;
}

interface SocialPlatformConnection {
  isConnected: boolean;
  token: string;
  username?: string;
  lastConnected?: Date;
  expiresAt?: number;
}

interface SocialConnections {
  linkedin?: SocialPlatformConnection;
  x?: SocialPlatformConnection;
}

interface AuthState {
  user: { 
    userId: string;
    username: string;
    email?: string;
    displayName: string;
    imgUrl: string | null;
    phone?: string;
    loginMethod: LoginMethod;
    socialConnections?: SocialConnections;
  } | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  telegramAuthData: TelegramAuthData | null;
}

const initialState: AuthState = {
  user: null,
  accessToken: null,
  isAuthenticated: false,
  telegramAuthData: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<{
      user: {
        userId: string;
        username: string;
        email?: string;
        phone?: string;
        displayName: string;
        imgUrl: string | null;
        loginMethod: LoginMethod;
        socialConnections?: SocialConnections;
      },
      accessToken: string,
      telegramAuthData?: TelegramAuthData,
    }>) {
      const { user, accessToken, telegramAuthData } = action.payload;
      state.user = user;
      state.accessToken = accessToken;
      state.isAuthenticated = true;
      
      if (telegramAuthData) {
        state.telegramAuthData = telegramAuthData;
        localStorage.setItem('telegramAuthData', JSON.stringify(telegramAuthData));
      }
      
      // Store complete user object in localStorage
      localStorage.setItem('authUser', JSON.stringify(state.user));
      localStorage.setItem('accessToken', accessToken);
    },

    // Connect a social platform
    connectSocialPlatform(
      state,
      action: PayloadAction<{
        platform: 'linkedin' | 'x';
        token: string;
        username?: string;
        expiresAt?: number;
      }>
    ) {
      if (state.user) {
        const { platform, token, username, expiresAt } = action.payload;
        
        // Initialize socialConnections if it doesn't exist
        if (!state.user.socialConnections) {
          state.user.socialConnections = {};
        }

        // Update the platform connection
        state.user.socialConnections[platform] = {
          isConnected: true,
          token,
          username,
          lastConnected: new Date(),
          expiresAt
        };

        // Update localStorage
        localStorage.setItem('authUser', JSON.stringify(state.user));
      }
    },

    // Disconnect a social platform
    disconnectSocialPlatform(
      state,
      action: PayloadAction<'linkedin' | 'x'>
    ) {
      if (state.user?.socialConnections) {
        const platform = action.payload;
        
        // Remove the platform connection
        if (state.user.socialConnections[platform]) {
          delete state.user.socialConnections[platform];
        }

        // Update localStorage
        localStorage.setItem('authUser', JSON.stringify(state.user));
      }
    },

    logout(state) {
      state.user = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      state.telegramAuthData = null;
      localStorage.removeItem('authUser');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('telegramAuthData');
    },

    loadUserFromStorage(state) {
      const user = localStorage.getItem('authUser');
      const accessToken = localStorage.getItem('accessToken');
      const telegramAuthData = localStorage.getItem('telegramAuthData');

      if (user && accessToken) {
        state.user = JSON.parse(user);
        state.accessToken = accessToken;
        state.isAuthenticated = true;
      }

      if (telegramAuthData) {
        state.telegramAuthData = JSON.parse(telegramAuthData);
      }
    },

    updateUserData: (state, action) => {
      if (state.user) {
        state.user = {
          ...state.user,
          ...action.payload
        };
        // Also update localStorage when user data is updated
        localStorage.setItem('authUser', JSON.stringify(state.user));
      }
    },
  },
});

export const { 
  setUser, 
  logout, 
  loadUserFromStorage,
  connectSocialPlatform,
  disconnectSocialPlatform,
  updateUserData
} = authSlice.actions;

export default authSlice.reducer;
export { LoginMethod };
